import { SWATCH_NA } from "@/constants"
import {
  getPDPUrlWithConfig,
  getSwatchCount,
  getSwatchPreset,
} from "@/services/server/plpHelper"

// calculate price for data layer
const calculateDataLayerPrices = (item, persona, colors) => {
  const price = item?.[`priceStartingAt.${persona}_d`] ?? null
  const isDiscountedPrice =
    item?.[`priceList.${persona}.discountedPrice_d`] ?? ""
  const discountAmount = price - isDiscountedPrice || 0
  const badgeVal = item?.[`priceList.${persona}.saleOffer_s`] ?? ""
  const discountPercent =
    badgeVal && isDiscountedPrice
      ? 100 - Math.round((isDiscountedPrice / price) * 100)
      : 0
  const globalPromotionPrice =
    discountPercent && discountAmount < price
      ? Number((price - discountAmount).toFixed(2))
      : price
  const productIsRetail = item?.["ProductIsRetail"] ?? false
  return {
    globalPromotionPrice,
    priceState:
      badgeVal && isDiscountedPrice
        ? "percentOff|" + discountPercent
        : "regular price",
    productBasePrice: price,
    productSalePrice: isDiscountedPrice || price,
    productSaleable: !(productIsRetail || !colors.length),
  }
}

const extractProductName = (
  item,
  isSterling,
  sterlingData,
  isGrouped,
  type
) => {
  if (isSterling && sterlingData?.length > 0) {
    return sterlingData[0]?.["ProductBrandName_s"] || ""
  }

  if (isGrouped && item?.["Collapse_Title_s"]) {
    return item["Collapse_Title_s"]
  }

  if (type?.toLowerCase() === "parts") {
    return item?.["productName_s"] || ""
  }

  return item?.["productName_s"] || item?.["RegionBrandName_s"] || ""
}

const getViewportWidth = () => {
  if (globalThis.window !== undefined) {
    return globalThis.window.innerWidth
  }
  // Default width for server-side rendering
  return 1024
}

export const getBadgesData = (product, configdata = {}) => {
  const plpBadges = getdefaultBades(configdata?.plpBadgeList)
  const abc = plpBadges.filter(item => {
    const isNew = item.key.includes("new") && showNewBadge(product.shipmentDate)
    const isSale = item.key.includes("sale") && !_isEmpty(product.badgeValue)
    const is3D = item.key.includes("3d") && isThreeDBadgeAvailable(product)
    const isExclusive =
      item.key.includes("exclusive") &&
      (parseToBoolean(product.productIsExclusive) ||
        parseToBoolean(product?.["ProductIsExclusive_s"]))
    const isSet =
      item.key.includes("bundleBadge") &&
      (product.bundleFlag === "Yes" || product.Bundle_s === "Yes")
    return isNew || isSale || is3D || isExclusive || isSet
  })
  return abc
}

const getProductUrl = (product, plpServletPath, general) => {
  if (!plpServletPath?.length || !product?.category || !product?.slug) {
    return ""
  }
  return getPDPUrlWithConfig(
    product.category,
    product.slug,
    plpServletPath,
    general
  )
}

export const colorFetcheslogic = (data, width, swatchUrl) => {
  let colorsTemp = []
  if (data.colors) {
    colorsTemp = data.colors.map(color => {
      const d = color?.split("|")
      // Use desktop preset for server-side rendering
      const preset = getSwatchPreset(width)
      const imgURL = `${swatchUrl}${d[1]}${preset}`
      const imgObj = { src: imgURL, alt: d[0] }
      const colorObj = {
        colorName: d[0],
        title: d[0],
        image: imgObj,
        shape: "square",
        size: "small",
        swatch: d[1],
        sku: d[2],
        isStock: d[3],
        isDiscontinued: d[4],
        customerFacingSKU: d[5],
        id: d[2],
        // All event handlers will be attached client-side
        onMouseEnter: null,
        onMouseLeave: null,
        productData: {
          sku: d[2],
          customerFacingSKU: d[5],
          image: imgURL,
          colorName: d[0],
          isStock: d[3],
        },
      }
      return colorObj
    })
  }

  const colorsTempVal = colorsTemp.filter(
    item =>
      item.swatch?.toLowerCase() !== SWATCH_NA.toLowerCase() &&
      (item.isDiscontinued === "false" ||
        Date.parse(item.isDiscontinued) > Date.now())
  )
  return colorsTempVal
}

const isThreeDBadgeAvailable = data => {
  return (
    data["resourceNvznId"] ||
    data["resourceVntanaProductId"] ||
    data["resourceVntanaProductIdList"]?.length ||
    data["resourceNvznIdList"]?.length
  )
}

export const getBadgeValue = (key, arry) => {
  const badgeInfo = arry.find(item => item.map.Key === key)
  const {
    map: { Key = "", Values: { myArrayList: badgeValue = [] } = {} } = {},
  } = badgeInfo
  return {
    key: Key,
    value: badgeValue[0] ?? "",
  }
}

const getdefaultBades = (plpBadgeList = []) => {
  const displayOrderBadges =
    plpBadgeList.find(item => item.map.Key === "displayOrderBadges") ?? {}
  const {
    map: { Values: { myArrayList: displayOrderBadgeList = [] } = {} } = {},
  } = displayOrderBadges

  const plpBadges = []
  displayOrderBadgeList.forEach(item => {
    const badgeData = getBadgeValue(item, plpBadgeList)
    plpBadges.push({ label: badgeData.value, key: badgeData.key }) // variant:badgeData.key,
  })
  return plpBadges
}

export const transformProductCards = async (
  products,
  configdata,
  swatchUrl,
  plpServletPath,
  general,
  selectedColor,
  plpPresetConfig
) => {
  const width = getViewportWidth()
  try {
    // Input validation
    if (!products || !Array.isArray(products)) {
      return []
    }
    // Transform products
    return products
      .map(product => {
        // Validate product
        if (!product) return null
        // Get SKU - use variantList.sku_ss[0] for bundles, otherwise use masterCustomerFacingSku or product.sku
        let sku = "K-" + product.sku || ""

        // For bundle products, use variantList.sku_ss[0] if available
        if (
          product.bundleFlag === "Yes" &&
          product.originalData?.["variantList.sku_ss"]?.[0]
        ) {
          sku = product.originalData["variantList.sku_ss"][0]
        }

        if (!sku) return null
        try {
          // Process product data
          // if selectColor the fetch sku based on that

          const selectedSku = product?.colors?.find(
            color => color?.split("|")[5] === selectedColor?.customerFacingSKU
          )
          if (selectedSku) sku = selectedSku?.split("|")[5]

          const allowedColorCount = getSwatchCount(true)
          const allColorsData = colorFetcheslogic(product, width, swatchUrl)
          let extraColors = 0
          if (allColorsData.length > allowedColorCount) {
            extraColors = allColorsData.length - allowedColorCount
          }
          const colors = allColorsData?.slice(0, allowedColorCount) || []
          const badgesdata = getBadgesData(product, configdata) || []
          const url = getProductUrl(product, plpServletPath, general)
          const fullUrl =
            typeof globalThis.window === "undefined"
              ? `${process.env.NEXT_PUBLIC_GLOBALCONFENV}${url}`
              : `${globalThis.window.location.origin}${url}`
          const isBundle = product.bundleFlag === "Yes"
          let swatchImageId = ""
          let swatchImages =
            product?.isCollapsePLP &&
            !isBundle &&
            !product.isBundleProduct &&
            product?.originaldata?.[`colorSkuSlugListDetails_ss`].length > 0
              ? product?.originaldata[`colorSkuSlugListDetails_ss`]
              : product?.variantImages

          swatchImages = swatchImages?.find(img =>
            selectedColor
              ? selectedColor.sku === img.split("|")[0]
              : product.sku === img.split("|")[0]
          )

          let imgUrl = ""
          let defaultImageName = ""
          if (swatchImages) {
            swatchImages = swatchImages?.split("|")
            const swatchIndex =
              product?.isCollapsePLP && !isBundle && !product.isBundleProduct
                ? swatchImages.length - 2
                : swatchImages.length - 1

            const swatchImageValues = swatchImages[swatchIndex]?.split("/")
            swatchImageId = swatchImageValues[swatchImageValues.length - 1]
            imgUrl = getPresetUrl(
              width,
              width <= 1024 ? 2 : 3,
              general.swatchUrl,
              swatchImageId,
              plpPresetConfig
            )
            defaultImageName = swatchImageId
          }
          // Create product object
          const newProductObj = {
            type: "Product Card",
            isGrouped: product.isGrouped,
            isCollapsePLP: product.isCollapsePLP,
            isSterling: product.isSterling,
            sterlingData: product.sterlingData || [],
            variantImages: product.productVariantImages,
            variantColors: product.colors,
            product: product,
            originaldata: product.originalData,
            fullUrl: fullUrl,
            defaultImageName: defaultImageName || "",
            image: {
              src: imgUrl,
              alt: product.productName || "Product Image",
            },
            isSaved: false,
            saveCTA: null,
            title: {
              text: product.productName || "",
            },
            description: {
              text: product.description || "",
            },

            productDetails: {
              colorSwatches: {
                colors,
                extraColors: extraColors,
                selectedColor: null,
                onColorSelected: null,
              },
              tags: badgesdata,
              SKU: { text: sku },
            },
          }
          return newProductObj
        } catch (error) {
          console.log(`Error processing product ${sku}:`, error)
          return null
        }
      })
      .filter(Boolean) // Remove null values
  } catch (error) {
    console.error("Error in transformProductCards:", error)
    return []
  }
}

export const getPlpProcessedData = async (
  inputObj = {},
  isSterling = false,
  sterlingData = [],
  type = ""
) => {
  const newData = {}
  const isGrouped = inputObj?.grouped
  const isCollapsePLP = inputObj?.fusion?.isCollapsePLP ?? false
  const persona =
    typeof getUserPersona === "function" ? getUserPersona() : "GST"
  // First process raw data
  const { total = 0 } = inputObj || {}
  newData.total = total
  Object.entries(inputObj).forEach(([key, value]) => {
    if (Array.isArray(value) && key === "data") {
      newData.data = value.map(item => {
        // Extract productName logic
        const productName = extractProductName(
          item,
          isSterling,
          sterlingData,
          isGrouped,
          type
        )

        // Extract pricing information
        const fieldValue = item?.["Collapse_Field_s"]
        const swatchColor = inputObj?.PlpSwatches.filter(
          swatchObj => swatchObj[fieldValue]
        )

        let colors
        if (inputObj?.fusion?.isCollapsePLP && swatchColor.length) {
          colors = swatchColor[0][fieldValue]
        } else if (item?.["Color.SKU.Details_ss"]) {
          colors = item?.["Color.SKU.Details_ss"]
        } else {
          colors = item?.[`Color.${persona}.Details_ss`] || []
        }

        const {
          globalPromotionPrice,
          priceState,
          productBasePrice,
          productSalePrice,
          productSaleable,
        } = calculateDataLayerPrices(item, persona, colors)
        // 1) Destructure everything you use from `item`
        const {
          ProductDescriptionProductShort_s: productShortDescription = "",

          // sku / bundle
          sku_s: sku,
          masterSKU_s: masterSKU = null,
          Bundle_s: bundleS = null,
          "variantList.sku_ss": variantListSkuSS = [], // NOTE: this key is unusual; see note below
          masterCustomerFacingSKU_s: masterCustomerFacingSKU = "",
          primaryBundleSKU_s: primaryBundleSKU = null,
          ctProductType_s: ctProductType = null,
          BundlePLPImageURL_s: bundlePLPImageURL = null,
          Bundle_s: BundleFlag, // alias if you want, optional

          // misc fields
          Collapse_Field_s: collapseField = "",
          ResourceNvznId_s: resourceNvznId = null,
          RegionIsBackOrderable_b: regionIsBackOrderable = false,
          ResourceVntanaProductId_s: resourceVntanaProductId = null,
          ResourceVntanaProductIdList_ss: resourceVntanaProductIdList = [],
          ResourceNvznIdList_ss: resourceNvznIdList = [],
          ProductIsExclusive_s: productIsExclusive = null,
          slug_s: slug = null,
          ProductProductNo_s: productNo = null,
          "ProductResource.InteractiveSVG.resourceName_ss": resourceNameSS = "",
          "ProductResource.InteractiveSVG.resourceTitle_ss":
            resourceTitleSS = "",
          Product_Category: productCategory = "",
          ProductCategory_s: productLocalCategory_s = "",
          RegionReleaseforShipment_dt: regionReleaseForShipment_dt = "",
          ProductIsRetail: productIsRetail = false,
          min_price: minPrice = null,

          // discontinued
          productRegionAllVariantDiscontinued_b,

          ProductBrandNameDisplay_s: productBrandNameDisplay = "",
          // images
          "productImages.labelWithUrl_ss": productImagesLabelWithUrlSS = [],
          "productImages.url_s": productImageUrl = "",
          "productImages.url_ss": productImagesUrlSS = [],
          category1_s: productRoom = "n/a",
          comboSKUAvailable_b: comboSKUAvailable_b = false,
          "productImage.url_ss": productSingleImageUrl = [],
          CustomerFacingSKU_s: customerFacingSKU = "",
        } = item ?? {}
        return {
          isCollapsePLP: isCollapsePLP,
          isSterling: isSterling,
          originalData: item,
          isGrouped: isGrouped,
          description: productShortDescription,
          colors: colors,
          collapseField,

          resourceNvznId,
          regionIsBackOrderable,
          resourceVntanaProductId,
          resourceVntanaProductIdList,
          resourceNvznIdList,
          productIsExclusive,
          slug,
          productNo,
          resourceName: resourceNameSS,
          resourceTitle: resourceTitleSS,
          productName: productName ?? null,
          category: productCategory || productLocalCategory_s,
          shipmentDate: regionReleaseForShipment_dt,
          badgeValue: item?.[`priceList.${persona}.saleOffer_s`] ?? "",
          productIsRetail: item?.["ProductIsRetail"] === "true",
          isSuperSku: comboSKUAvailable_b,
          productRoom,
          productCollectionsName: productBrandNameDisplay,
          productVariantImages: productImagesLabelWithUrlSS,
          sku:
            bundleS === "Yes" && variantListSkuSS.length
              ? variantListSkuSS[0]
              : (sku ?? masterSKU),
          masterCustomerFacingSKU,
          productCategory,
          isBundleProduct:
            Boolean(primaryBundleSKU) ||
            ctProductType === "static-bundle-parent",
          bundlePLPImageURL: bundlePLPImageURL,
          bundleFlag: bundleS,
          displayPriceStartingAt: item?.["min_price"] ?? null,
          globalPromotionPrice,
          priceState,
          productBasePrice,
          productSalePrice,
          productSaleable,
          allVariantsDiscontinued: Boolean(
            item?.["productRegionAllVariantDiscontinued_b"]
          ),
          productImageUrl,
          productImagesUrlSS,
          masterSKU,
          productSingleImageUrl,
          customerFacingSKU,
        }
      })
    }
  })
  return {
    ...newData,
  }
}
